import ApiError from "@components/GenericApiError/Error";
import Grid from "@components/Grid/Grid";
import { IsLoggedIn } from "@components/GuestView/IsLoggedIn";
import Link from "@components/Link/Link";
import Loader from "@components/Loader/Loader";
import getCookie from "@utilities/getCookie";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../context/user";
import { getSavedPages, removeSavedPage } from "../../services/forms";
import Cta from "../Cta/Cta";
import styles from "./C66_MyProfileSavedContent.module.scss";

interface SavedItems {
  pages: SavedItem[];
  docs: SavedItem[];
}

type SavedItemType = "entry" | "asset";

interface SavedItem {
  url: string;
  title: string;
  description: string;
  id: string;
  type: SavedItemType;
}

export interface SavedContentProps {
  removeText: string;
  savedPagesText: string;
  savedDocumentsText: string;
  noItemsText: string;
}

const ANNOUNCEMENT_DELAY = 1000;

export const SavedContent = (props: { content: SavedContentProps }) => {
  const { content } = props;
  const { removeText, savedPagesText, savedDocumentsText, noItemsText } =
    content;
  const [saved, setSaved] = useState<SavedItems | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [ariaMessage, setAriaMessage] = useState("");

  const { user } = useContext(UserContext);
  const removeButtonRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const announcementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    const getSaved = async () => {
      if (user) {
        try {
          const savedPages = await getSavedPages(getCookie("access_token"));

          const mapSavedPages = await Promise.all(
            savedPages?.result?.map(async (item) => {
              return {
                ...(await (
                  await fetch(`/api/getEntryOrAssetById?id=${item}`)
                ).json()),
                id: item,
              };
            })
          );

          setSaved({
            pages: mapSavedPages.filter((item) => item.type === "entry"),
            docs: mapSavedPages.filter((item) => item.type === "asset"),
          });
        } catch (e) {
          console.log(e);
          setError(true);
        }
      }
    };

    getSaved();
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = null;
      }
    };
  }, []);

  // Function for safe announcement
  const announce = useCallback((message: string) => {
    if (announcementTimeoutRef.current) {
      clearTimeout(announcementTimeoutRef.current);
      announcementTimeoutRef.current = null;
    }

    announcementTimeoutRef.current = setTimeout(() => {
      setAriaMessage(message);

      // Clear the message after 1 second
      setTimeout(() => {
        setAriaMessage("");
      }, 5000);
    }, ANNOUNCEMENT_DELAY);
  }, []);

  const removeItem = useCallback(
    async (id: string, type: SavedItemType) => {
      const listKey = type === "entry" ? "pages" : "docs";
      const list = saved?.[listKey];

      if (!list) return;

      const currentIndex = list.findIndex((item) => item.id === id);
      const item = list[currentIndex];

      if (!item) return;

      const itemTitle = item.title || item.url || "Item";

      const nextItem = list[currentIndex + 1];
      const previousItem = list[currentIndex - 1];

      const focusTargetId = nextItem?.id || previousItem?.id || null;

      const focusAfterStateUpdate = () => {
        requestAnimationFrame(() => {
          if (focusTargetId && removeButtonRefs.current[focusTargetId]) {
            removeButtonRefs.current[focusTargetId]?.focus();
            return;
          }

          document.getElementById("lblEmptyData")?.focus();
        });
      };

      const focusRestoredItem = () => {
        requestAnimationFrame(() => {
          removeButtonRefs.current[id]?.focus();
        });
      };

      // Optimistic remove from state
      setSaved((prevSaved) => {
        if (!prevSaved) return prevSaved;

        return {
          ...prevSaved,
          [listKey]: prevSaved[listKey].filter((item) => item.id !== id),
        };
      });

      focusAfterStateUpdate();

      try {
        const result = await removeSavedPage(id, getCookie("access_token"));

        if (!result || result.status < 200 || result.status >= 300) {
          throw new Error("Failed to remove saved item");
        }

        if (focusTargetId) {
          announce(`${itemTitle} removed. Focus moved to another saved item.`);
        } else {
          announce(`${itemTitle} removed. Focus moved to section heading.`);
        }
      } catch (error) {
        // Restore only the removed item
        setSaved((prevSaved) => {
          if (!prevSaved) return prevSaved;

          const currentList = prevSaved[listKey];

          // Avoid duplicate restore
          if (currentList.some((item) => item.id === id)) {
            return prevSaved;
          }

          const restoredList = [...currentList];
          restoredList.splice(currentIndex, 0, item);

          return {
            ...prevSaved,
            [listKey]: restoredList,
          };
        });

        focusRestoredItem();

        console.error("Error removing saved item:", error);
        announce(`Error removing ${itemTitle}. The item has been restored.`);
      }
    },
    [saved, announce]
  );

  if (error) {
    return <ApiError />;
  }

  if (!saved) {
    return (
      <div className={styles["spinner"]}>
        <Loader
          color="#001e62"
          size={70}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      </div>
    );
  }
  const hasNoItems = !saved.docs.length && !saved.pages.length;

  return (
    <div className="wrapper">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {ariaMessage}
      </div>

      <Grid row>
        <Grid column md={8} sm={12}>
          <div className={styles["my-profile-saved-content"]}>
            <div className={styles.coont}>
              {saved.pages.length > 0 && (
                <>
                  <h2 id="savedPagesHeading" tabIndex={-1}>
                    {savedPagesText}
                  </h2>
                  <ul>
                    {saved.pages.map((page) => {
                      return (
                        <li key={page.id} className="relative">
                          <div className="md:flex justify-between w-full">
                            <div className={styles["copy-cs"]}>
                              <h3>
                                <Link
                                  href={`/${page.url}`}
                                  ref={(el) => {
                                    removeButtonRefs.current[page.id] =
                                      el as HTMLAnchorElement | null;
                                  }}
                                >
                                  {page.title}
                                </Link>
                              </h3>
                              <p className={styles["sub-copy"]}>
                                {page.description}
                              </p>
                            </div>
                            <div className={styles.remove}>
                              <Cta
                                renderAs="button"
                                url="#"
                                linkText={removeText}
                                type="tertiary"
                                onClick={() => removeItem(page.id, page.type)}
                                icon="icon-cross"
                                ariaLabel={`${removeText} ${page.title || page.url || ""}`}
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {saved.docs.length > 0 && (
                <>
                  <h2 id="savedDocsHeading" tabIndex={-1}>
                    {savedDocumentsText}
                  </h2>
                  <ul>
                    {saved.docs.map((doc) => {
                      return (
                        <li key={doc.id} className="relative">
                          <div className="flex justify-between w-full">
                            <div className={styles["copy-cs"]}>
                              <h3>
                                <a
                                  href={`${doc.url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  ref={(el) => {
                                    removeButtonRefs.current[doc.id] =
                                      el as HTMLAnchorElement | null;
                                  }}
                                >
                                  {doc.title}
                                </a>
                              </h3>
                            </div>
                            <div className={styles.remove}>
                              <Cta
                                renderAs="button"
                                url="#"
                                linkText={removeText}
                                type="tertiary"
                                onClick={() => removeItem(doc.id, doc.type)}
                                icon="icon-cross"
                                ariaLabel={`${removeText} ${doc.title || doc.url || ""}`}
                              />
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {hasNoItems && (
                <h2 id="lblEmptyData" tabIndex={-1}>
                  {noItemsText}
                </h2>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default IsLoggedIn(SavedContent);
